import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { FileText, Plus } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';

const MedicalNotesPanel = ({ memberId, userRole }) => {
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchNotes();
  }, [memberId]);

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${API}/medical-notes/member/${memberId}`);
      setNotes(response.data);
    } catch (error) {
      console.error('Failed to fetch notes');
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/medical-notes`, {
        member_id: memberId,
        note: newNote
      });
      toast.success('Note added successfully');
      setNewNote('');
      fetchNotes();
    } catch (error) {
      toast.error('Failed to add note');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card data-testid="medical-notes-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          Medical Notes
        </CardTitle>
        <CardDescription>Patient medical history and notes</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {(userRole === 'admin' || userRole === 'staff') && (
          <div className="space-y-2">
            <Textarea
              placeholder="Add a new medical note..."
              value={newNote}
              onChange={(e) => setNewNote(e.target.value)}
              rows={3}
              data-testid="new-note-input"
            />
            <Button
              onClick={handleAddNote}
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700"
              data-testid="add-note-btn"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Note
            </Button>
          </div>
        )}

        <div className="space-y-3 max-h-96 overflow-y-auto">
          {notes.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No medical notes yet</p>
          ) : (
            notes.map((note) => (
              <div key={note.id} className="border rounded-lg p-3 bg-gray-50" data-testid={`note-${note.id}`}>
                <div className="flex justify-between items-start mb-2">
                  <span className="text-sm font-medium text-gray-700">{note.created_by_name}</span>
                  <span className="text-xs text-gray-500">
                    {new Date(note.created_at).toLocaleDateString()} {new Date(note.created_at).toLocaleTimeString()}
                  </span>
                </div>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{note.note}</p>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MedicalNotesPanel;
